/**
 * STARS — seeded night star field (night.html #stars): size buckets, glow, gentle
 * drift. Default path bakes a tile once (glow included) and scrolls it each frame
 * without per-star twinkle. Legacy live draw available via opts.legacy === true.
 */

import { normalizeTimeOfDay, timeSec } from "./sky-utils.js";
import { combineSkyDecorSeed, seededHash01 } from "../../core/day-night.js";

export var STAR_COUNT = 140;
/** Horizontal drift speed (CSS px/s), matches the legacy live path. */
export var STAR_DRIFT_PX_PER_SEC = 1.5;
/** Baked tile resolution cap; display blit still uses the canvas DPR transform. */
export var STAR_TILE_MAX_DPR = 1;

var _starTile = {
  key: null,
  canvas: null,
  ctx: null,
  pixelW: 0,
  pixelH: 0,
};

/** Night star visibility: fade in dusk→night, out near dawn. */
export function starFieldAlpha(timeOfDay) {
  var t = normalizeTimeOfDay(timeOfDay);
  if (t <= 0.55) return 0;
  if (t < 0.7) return (t - 0.55) / 0.15;
  if (t <= 0.95) return 1;
  if (t < 1) return Math.max(0, 1 - (t - 0.95) / 0.05);
  return 0;
}

export function resetStarFieldCache() {
  _starTile.key = null;
  _starTile.canvas = null;
  _starTile.ctx = null;
  _starTile.pixelW = 0;
  _starTile.pixelH = 0;
}

function starTileKey(w, h, scale, decorSeed, bakeDpr) {
  return w + "x" + h + "@" + scale.toFixed(3) + "|" + String(decorSeed) + "|dpr" + bakeDpr;
}

/** @internal Test hook — current baked tile cache key, or null. */
export function getStarTileCacheKeyForTests() {
  return _starTile.key;
}

function paintStarField(tileCtx, w, h, scale, decorSeed) {
  tileCtx.clearRect(0, 0, w, h);
  tileCtx.save();
  tileCtx.shadowColor = "rgba(255, 255, 255, 0.8)";
  for (var i = 0; i < STAR_COUNT; i++) {
    var s = decorSeed != null ? combineSkyDecorSeed(decorSeed, i + 101) : i + 101;
    var hx = seededHash01(s, 3.17);
    var hy = seededHash01(s, 8.41);
    var hs = seededHash01(s, 5.93);
    var hroll = seededHash01(s, 4.62);
    var sx = hx * w;
    var ty = i < STAR_COUNT * 0.4 ? (0.72 + hy * 0.28) : hy;
    var sy = ty * h;
    var baseDia = hroll < 0.55 ? 2 : hroll < 0.9 ? 3 : 4;
    var radius = (baseDia * 0.5) * (0.6 + hs * 1.1) * scale;
    // Fixed brightness (no twinkle): mid-point of the legacy 0.55..1.0 twinkle range.
    var sa = (0.3 + 0.65 * hs) * 0.775;
    if (sa <= 0.003) continue;
    tileCtx.shadowBlur = 4 * scale;
    tileCtx.fillStyle = "rgba(255, 255, 255, " + sa.toFixed(3) + ")";
    tileCtx.beginPath();
    tileCtx.arc(sx, sy, radius, 0, Math.PI * 2);
    tileCtx.fill();
  }
  tileCtx.restore();
}

function ensureStarTile(w, h, scale, decorSeed, dpr) {
  var safeDpr = dpr > 0 ? dpr : 1;
  var bakeDpr = Math.min(safeDpr, STAR_TILE_MAX_DPR > 0 ? STAR_TILE_MAX_DPR : 1);
  var key = starTileKey(w, h, scale, decorSeed, bakeDpr);
  if (_starTile.key === key && _starTile.canvas && _starTile.ctx) {
    return _starTile;
  }
  if (typeof document === "undefined") return null;
  var pixelW = Math.max(1, Math.round(w * bakeDpr));
  var pixelH = Math.max(1, Math.round(h * bakeDpr));
  if (!_starTile.canvas) {
    _starTile.canvas = document.createElement("canvas");
    _starTile.ctx = _starTile.canvas.getContext("2d");
  }
  var canvas = _starTile.canvas;
  var tileCtx = _starTile.ctx;
  if (!tileCtx) return null;
  if (canvas.width !== pixelW || canvas.height !== pixelH) {
    canvas.width = pixelW;
    canvas.height = pixelH;
  }
  tileCtx.setTransform(bakeDpr, 0, 0, bakeDpr, 0, 0);
  paintStarField(tileCtx, w, h, scale, decorSeed);
  _starTile.key = key;
  _starTile.pixelW = pixelW;
  _starTile.pixelH = pixelH;
  return _starTile;
}

/**
 * Baked 140-star tile for the GPU sky (same field as the Canvas 2D blit).
 * Height is the sky band above the sea line. Null when the band is empty.
 */
export function getStarFieldTile(width, height, decorSeed, dpr) {
  var w = Math.max(0, Number(width) || 0);
  var h = Math.max(0, Number(height) || 0);
  if (w <= 0 || h <= 0) return null;
  var scale = Math.max(0.5, w / 720);
  return ensureStarTile(w, h, scale, decorSeed, dpr);
}

function blitScrollingStarTile(ctx, tile, bounds, driftPx, fieldAlpha) {
  var x = bounds.x;
  var y = bounds.y;
  var w = bounds.width;
  var h = bounds.height;
  var off = ((driftPx % w) + w) % w;
  var prevAlpha = ctx.globalAlpha;
  ctx.globalAlpha = prevAlpha * fieldAlpha;
  ctx.drawImage(tile.canvas, 0, 0, tile.pixelW, tile.pixelH, x - off, y, w, h);
  ctx.drawImage(tile.canvas, 0, 0, tile.pixelW, tile.pixelH, x - off + w, y, w, h);
  ctx.globalAlpha = prevAlpha;
}

function drawStarsLegacy(ctx, bounds, timeOfDay, currentTime, decorSeed) {
  var a = starFieldAlpha(timeOfDay);
  if (a <= 0.01) return;
  var x = bounds.x;
  var y = bounds.y;
  var w = Math.max(0, bounds.width);
  var h = Math.max(0, bounds.height);
  if (w <= 0 || h <= 0) return;
  var ts = timeSec(currentTime);
  var scale = Math.max(0.5, w / 720);
  ctx.save();
  ctx.shadowColor = "rgba(255, 255, 255, 0.8)";
  for (var i = 0; i < STAR_COUNT; i++) {
    var s = decorSeed != null ? combineSkyDecorSeed(decorSeed, i + 101) : i + 101;
    var hx = seededHash01(s, 3.17);
    var hy = seededHash01(s, 8.41);
    var hs = seededHash01(s, 5.93);
    var hp = seededHash01(s, 1.27);
    var hroll = seededHash01(s, 4.62);
    var sx = x + ((hx * w + ts * STAR_DRIFT_PX_PER_SEC) % w);
    var ty = i < STAR_COUNT * 0.4 ? (0.72 + hy * 0.28) : hy;
    var sy = y + ty * h;
    var baseDia = hroll < 0.55 ? 2 : hroll < 0.9 ? 3 : 4;
    var radius = (baseDia * 0.5) * (0.6 + hs * 1.1) * scale;
    var twinkle = 0.5 + 0.5 * Math.sin(ts * 0.8 + hp * 6.2832);
    var sa = a * (0.3 + 0.65 * hs) * (0.55 + 0.45 * twinkle);
    ctx.shadowBlur = 4 * scale;
    ctx.fillStyle = "rgba(255, 255, 255, " + sa.toFixed(3) + ")";
    ctx.beginPath();
    ctx.arc(sx, sy, radius, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {{ x: number, y: number, width: number, height: number }} bounds sky band above the sea
 * @param {number} [opts.dpr] device pixel ratio for the baked tile (default 1)
 */
export function drawStars(ctx, bounds, timeOfDay, currentTime, decorSeed, opts) {
  if (!ctx || !bounds) return;
  opts = opts || {};
  if (opts.legacy === true) {
    drawStarsLegacy(ctx, bounds, timeOfDay, currentTime, decorSeed);
    return;
  }
  var a = starFieldAlpha(timeOfDay);
  if (a <= 0.01) return;
  var w = Math.max(0, bounds.width);
  var h = Math.max(0, bounds.height);
  if (w <= 0 || h <= 0) return;
  var scale = Math.max(0.5, w / 720);
  var dpr = opts.dpr != null ? Number(opts.dpr) : 1;
  var tile = ensureStarTile(w, h, scale, decorSeed, dpr);
  if (!tile) {
    drawStarsLegacy(ctx, bounds, timeOfDay, currentTime, decorSeed);
    return;
  }
  var driftPx = timeSec(currentTime) * STAR_DRIFT_PX_PER_SEC;
  blitScrollingStarTile(ctx, tile, bounds, driftPx, a);
}